import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ArrowIcon from 'assets/LeadInfo/ArrowIcon';
import UserIconComponent from 'assets/LeadInfo/UserIcon';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import { bulkAssignLeads, bulkReassignLeads } from 'features/newsales/newsalesActions';
import { getSubordinates } from 'features/users/usersActions';
import { selectSubordinates, selectSubordinatesLoading } from 'features/users/usersSlice';
import useDebounce from 'hooks/useDebounce';

import {
  CreatorName,
  EmptyMessage,
  ErrorMessage,
  LeadCreatorSection,
  LoadingMessage,
  ModalContent,
  SearchWrapper,
  UserAvatar,
  UserInfo,
  UserItem,
  UserLeft,
  UserName,
  UserRole,
  UsersList,
} from './AssignLeadModal.styles';

const AssignLeadModal = ({ isOpen, leadData, onSuccess, mode = 'assign', onClose }) => {
  const dispatch = useDispatch();
  const users = useSelector(selectSubordinates);
  const isLoading = useSelector(selectSubordinatesLoading);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isReassignMode = mode === 'reassign';
  const title = isReassignMode ? 'Reassign Lead' : 'Assign Lead';

  useEffect(() => {
    if (!isOpen) return;
    fetchUsers(debouncedSearchTerm, false);
  }, [isOpen, debouncedSearchTerm]);

  const fetchUsers = async (search = '') => {
    setError('');
    try {
      const params = {};
      if (search && search.trim()) {
        params.search = search.trim();
      }
      await dispatch(getSubordinates(params)).unwrap();
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const leadIds = Array.isArray(leadData) ? leadData : [leadData?.id];
      if (isReassignMode) {
        await dispatch(
          bulkReassignLeads({
            leadIds,
            newAssigneeUuid: selectedUserId,
          })
        ).unwrap();
      } else {
        await dispatch(
          bulkAssignLeads({
            leadIds,
            assigneeUuid: selectedUserId,
          })
        ).unwrap();
      }

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err) {
      setError(
        err?.message || err || `Failed to ${isReassignMode ? 'reassign' : 'assign'} lead`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId(null);
    setSelectedUser(null);
    setSearchTerm('');
    setError('');
    onClose();
  };

  const handleSelect = (e) => {
    setSelectedUser(e);
    setSelectedUserId(e.uuid);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      footer={true}
      width="480px"
      height={'80%'}
      closeIcon={true}
      onOkLoading={isSubmitting}
      onOk={handleSubmit}
    >
      <ModalContent>
        {selectedUser?.name && (
          <LeadCreatorSection>
            <CreatorName>Lead assigned to {selectedUser?.name}</CreatorName>
          </LeadCreatorSection>
        )}

        <SearchWrapper>
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            height="44px"
            disabled={isSubmitting}
          />
        </SearchWrapper>

        {isLoading ? (
          <LoadingMessage>Loading users...</LoadingMessage>
        ) : users.length === 0 ? (
          <EmptyMessage>
            {searchTerm ? 'No users found matching your search' : 'No users available'}
          </EmptyMessage>
        ) : (
          <UsersList>
            {users.map((user) => {
              return (
                <UserItem
                  key={user.uuid}
                  $selected={selectedUserId === user.uuid}
                  onClick={() => handleSelect(user)}
                >
                  <UserLeft>
                    <UserAvatar>
                      <UserIconComponent width={20} height={20} />
                    </UserAvatar>
                    <UserInfo>
                      <UserName>
                        {user.name} {user.surname}
                      </UserName>
                      <UserRole>
                        {user.role || 'Team Member'}
                        {user.occupation && ` • ${user.occupation}`}
                      </UserRole>
                    </UserInfo>
                  </UserLeft>
                  <ArrowIcon width={20} height={20} />
                </UserItem>
              );
            })}
          </UsersList>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </ModalContent>
    </Modal>
  );
};

export default AssignLeadModal;
